// sRGB to linear RGB conversion constants (per IEC 61966-2-1 / sRGB standard)
// Threshold below which the linear approximation is used
const SRGB_LINEAR_THRESHOLD = 0.03928;
// Divisor for the linear portion of the transfer function
const SRGB_LINEAR_DIVISOR = 12.92;
// Offset added before applying the gamma curve
const SRGB_GAMMA_OFFSET = 0.055;
// Divisor for the gamma portion of the transfer function
const SRGB_GAMMA_DIVISOR = 1.055;
// Gamma exponent for sRGB
const SRGB_GAMMA = 2.4;

// Relative luminance coefficients (per WCAG 2.1 / ITU-R BT.709)
// These weights reflect human eye sensitivity: green > red > blue
const LUMINANCE_R = 0.2126;
const LUMINANCE_G = 0.7152;
const LUMINANCE_B = 0.0722;
const OPAQUE_HEX_COLOR_PATTERN = /^#[\dA-Fa-f]{6}$/;
const ALPHA_HEX_COLOR_PATTERN = /^#([\dA-Fa-f]{6})([\dA-Fa-f]{2})$/;

export function flattenToOpaqueHex(color: string): string | null {
  if (OPAQUE_HEX_COLOR_PATTERN.test(color)) return color;

  const alphaMatch = ALPHA_HEX_COLOR_PATTERN.exec(color);
  if (!alphaMatch?.[1] || !alphaMatch[2]) return null;

  const alpha = parseInt(alphaMatch[2], 16) / 255;
  const channels = [0, 2, 4].map((offset) => {
    const channel = parseInt(alphaMatch[1]!.substring(offset, offset + 2), 16);
    return Math.round(channel * alpha + 255 * (1 - alpha));
  });
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

// Helper to parse RGB values from color string
function parseColor(color: string): { r: number; g: number; b: number } {
  // Check if it's an RGB/RGBA format
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbMatch && rgbMatch[1] && rgbMatch[2] && rgbMatch[3]) {
    return {
      r: parseInt(rgbMatch[1], 10) / 255,
      g: parseInt(rgbMatch[2], 10) / 255,
      b: parseInt(rgbMatch[3], 10) / 255,
    };
  }

  // Parse hex color
  const hex = color.replace('#', '');
  return {
    r: parseInt(hex.substring(0, 2), 16) / 255,
    g: parseInt(hex.substring(2, 4), 16) / 255,
    b: parseInt(hex.substring(4, 6), 16) / 255,
  };
}

// Helper to calculate relative luminance of a color (supports hex and rgb/rgba formats)
function getRelativeLuminance(color: string): number {
  const { r, g, b } = parseColor(color);

  // Convert to linear RGB
  const toLinear = (channel: number) => {
    return channel <= SRGB_LINEAR_THRESHOLD
      ? channel / SRGB_LINEAR_DIVISOR
      : ((channel + SRGB_GAMMA_OFFSET) / SRGB_GAMMA_DIVISOR) ** SRGB_GAMMA;
  };

  const rLinear = toLinear(r);
  const gLinear = toLinear(g);
  const bLinear = toLinear(b);

  // Calculate relative luminance
  return LUMINANCE_R * rLinear + LUMINANCE_G * gLinear + LUMINANCE_B * bLinear;
}

export function getColorContrastRatio(firstColor: string, secondColor: string): number {
  const firstLuminance = getRelativeLuminance(firstColor);
  const secondLuminance = getRelativeLuminance(secondColor);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

export function sortColorsByLuminance(colors: string[]): string[] {
  return colors.sort((a, b) => getRelativeLuminance(a) - getRelativeLuminance(b));
}
